import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import adminService from '../../services/adminService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

function AddMember() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    profession: '',
    organization: '',
    designation: '',
    experience: '',
    ibbiRegNumber: '',
    rvoName: '',
    membershipType: 'primary_member',
    status: 'active'
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.membershipType) newErrors.membershipType = 'Please select a membership type';
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSuccessMessage('');
    
    try {
      const response = await adminService.createMember(formData);
      
      // Show success message with membership details
      setSuccessMessage(`Member created successfully! Membership Number: ${response.membershipNumber}`);
      setEmailSent(response.emailSent || false);
      
      // Redirect to members list after 4 seconds
      setTimeout(() => {
        navigate('/admin/members');
      }, 4000);
      
    } catch (error) {
      console.error('Error adding member:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add member. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      {/* Loading Overlay */}
      {isSubmitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #002147',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{
            marginTop: '1.5rem',
            color: 'white',
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            Creating member and sending credentials...
          </div>
        </div>
      )}

      <div className="admin-page-header">
        <h1 className="admin-page-title">Add Member</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/members">Members</Link>
          </li>
          <li className="admin-breadcrumb-item active">Add Member</li>
        </ul>
      </div>
      
      {successMessage && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '1.5rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #c3e6cb'
        }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            ✓ {successMessage}
          </div>
          <div style={{ 
            backgroundColor: '#fff', 
            padding: '1rem', 
            borderRadius: '4px',
            border: '1px solid #c3e6cb',
            marginTop: '0.75rem'
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.5rem', color: emailSent ? '#28a745' : '#ffc107' }} />
              <strong>{emailSent ? 'Credentials Sent Successfully' : 'Email Status'}</strong>
            </div>
            <div style={{ fontSize: '0.95rem', marginTop: '0.5rem' }}>
              {emailSent ? (
                <span style={{ color: '#155724' }}>
                  ✉️ Login credentials have been sent to <strong>{formData.email}</strong>
                </span>
              ) : (
                <span style={{ color: '#856404' }}>
                  ⚠️ Member created but email could not be sent. Please share credentials manually with {formData.email}
                </span>
              )}
            </div>
          </div>
          <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
            Redirecting to members list in 4 seconds...
          </div>
        </div>
      )}

      {errors.submit && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f5c6cb'
        }}>
          {errors.submit}
        </div>
      )}

      {/* Personal Information Section */}
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-header" style={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '2px solid #dee2e6',
          padding: '1rem 1.5rem'
        }}>
          <h2 className="admin-card-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Personal Information
          </h2>
        </div>
        <div className="admin-card-body" style={{ padding: '2rem' }}>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-row" style={{ marginBottom: '1.5rem' }}>
              <div className="admin-form-group">
                <label htmlFor="firstName" className="admin-form-label">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.firstName ? 'admin-input-error' : ''}`}
                  placeholder="Enter first name"
                />
                {errors.firstName && <div className="admin-error-message">{errors.firstName}</div>}
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="lastName" className="admin-form-label">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.lastName ? 'admin-input-error' : ''}`}
                  placeholder="Enter last name"
                />
                {errors.lastName && <div className="admin-error-message">{errors.lastName}</div>}
              </div>
            </div>
            
            <div className="admin-form-row" style={{ marginBottom: '1.5rem' }}>
              <div className="admin-form-group">
                <label htmlFor="email" className="admin-form-label">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.email ? 'admin-input-error' : ''}`}
                  placeholder="member@example.com"
                />
                {errors.email && <div className="admin-error-message">{errors.email}</div>}
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="phone" className="admin-form-label">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.phone ? 'admin-input-error' : ''}`}
                  placeholder="10-digit mobile number"
                />
                {errors.phone && <div className="admin-error-message">{errors.phone}</div>}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Address Information Section */}
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-header" style={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '2px solid #dee2e6',
          padding: '1rem 1.5rem'
        }}>
          <h2 className="admin-card-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Address Information
          </h2>
        </div>
        <div className="admin-card-body" style={{ padding: '2rem' }}>
          <div className="admin-form-group" style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="address" className="admin-form-label">Street Address</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="admin-form-control"
              rows="3"
              placeholder="Enter complete address"
            />
          </div>

          <div className="admin-form-row" style={{ marginBottom: '0' }}>
            <div className="admin-form-group">
              <label htmlFor="city" className="admin-form-label">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="Enter city"
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="state" className="admin-form-label">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="Enter state"
              />
            </div>

            <div className="admin-form-group">
              <label htmlFor="pincode" className="admin-form-label">Pincode</label>
              <input
                type="text"
                id="pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="6-digit pincode"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Professional Information Section */}
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-header" style={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '2px solid #dee2e6',
          padding: '1rem 1.5rem'
        }}>
          <h2 className="admin-card-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Professional Information
          </h2>
        </div>
        <div className="admin-card-body" style={{ padding: '2rem' }}>
          <div className="admin-form-row" style={{ marginBottom: '1.5rem' }}>
            <div className="admin-form-group">
              <label htmlFor="profession" className="admin-form-label">Profession</label>
              <input
                type="text"
                id="profession"
                name="profession"
                value={formData.profession}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="e.g., Valuer, Consultant"
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="designation" className="admin-form-label">Designation</label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="e.g., Senior Valuer"
              />
            </div>
          </div>
          
          <div className="admin-form-row" style={{ marginBottom: '1.5rem' }}>
            <div className="admin-form-group">
              <label htmlFor="organization" className="admin-form-label">Organization</label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="Company/Organization name"
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="experience" className="admin-form-label">Years of Experience</label>
              <select
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                className="admin-form-control"
              >
                <option value="">Select experience</option>
                <option value="0-2">0-2 years</option>
                <option value="3-5">3-5 years</option>
                <option value="6-10">6-10 years</option>
                <option value="10+">More than 10 years</option>
              </select>
            </div>
          </div>

          <div className="admin-form-row" style={{ marginBottom: '0' }}>
            <div className="admin-form-group">
              <label htmlFor="ibbiRegNumber" className="admin-form-label">IBBI Registration Number</label>
              <input
                type="text"
                id="ibbiRegNumber"
                name="ibbiRegNumber"
                value={formData.ibbiRegNumber}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="Enter IBBI number"
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="rvoName" className="admin-form-label">RVO Name</label>
              <input
                type="text"
                id="rvoName"
                name="rvoName"
                value={formData.rvoName}
                onChange={handleChange}
                className="admin-form-control"
                placeholder="Registered Valuers Organization"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Membership Details Section */}
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-header" style={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '2px solid #dee2e6',
          padding: '1rem 1.5rem'
        }}>
          <h2 className="admin-card-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
            Membership Details
          </h2>
        </div>
        <div className="admin-card-body" style={{ padding: '2rem' }}>
          <div className="admin-form-row" style={{ marginBottom: '1.5rem' }}>
            <div className="admin-form-group">
              <label htmlFor="membershipType" className="admin-form-label">Membership Type *</label>
              <select
                id="membershipType"
                name="membershipType"
                value={formData.membershipType}
                onChange={handleChange}
                className={`admin-form-control ${errors.membershipType ? 'admin-input-error' : ''}`}
              >
                <option value="primary_member">Primary Member</option>
                <option value="associate_member">Associate Member</option>
                <option value="student_membership">Student Member</option>
                <option value="financial_collegium">Financial Collegium</option>
                <option value="academic_collegium">Academic Collegium</option>
                <option value="institutional_collegium">Institutional Collegium</option>
              </select>
              {errors.membershipType && <div className="admin-error-message">{errors.membershipType}</div>}
            </div>

            <div className="admin-form-group">
              <label htmlFor="status" className="admin-form-label">Account Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="admin-form-control"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={{
            backgroundColor: '#e7f3ff',
            border: '1px solid #b3d9ff',
            borderRadius: '4px',
            padding: '1rem',
            marginBottom: '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#0056b3' }} />
              <strong style={{ color: '#004085' }}>Membership Validity</strong>
            </div>
            <p style={{ margin: 0, color: '#004085', fontSize: '0.9rem' }}>
              Membership will be valid until <strong>March 31st</strong> of {new Date().getMonth() > 2 ? 'next year' : 'this year'}. 
              The membership number will be generated automatically upon creation.
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="admin-card">
        <div className="admin-card-body" style={{ padding: '1.5rem' }}>
          <div className="admin-form-actions" style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '1rem',
            margin: 0 
          }}>
            <button
              type="button"
              className="admin-btn admin-btn-light"
              onClick={() => navigate('/admin/members')}
              disabled={isSubmitting}
              style={{ minWidth: '120px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={isSubmitting}
              onClick={handleSubmit}
              style={{ 
                minWidth: '120px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {isSubmitting && (
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
              )}
              {isSubmitting ? 'Creating...' : 'Create Member'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AddMember;
